import * as React from 'react'
import { Button, Dropdown, Menu, Icon, Checkbox, DatePicker, Alert } from 'antd'
import { Tooltip, Select } from 'antd-v4'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { DateFilter } from './DateFilter'
import { ReportFilterStore, GroupByOptions, FixedDatesOptions } from '../ReportFilterStore'
import { ILocation } from '@getgreenline/homi-shared'
import moment from 'moment'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

import { browserHistory } from 'react-router'
import { ArrowRightOutlined } from '@ant-design/icons'
import { NestedCategoryFilter } from './CategoryFilter'
import { SelectProps } from 'antd-v4/lib/select'

const dateRangeLimitInMonths = 3

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  reportFilterStore?: ReportFilterStore
  hideDates?: boolean
  showSingleDate?: boolean
  infoMessage?: React.ReactElement
  apply?: () => void
  showGroupBy?: boolean
  onChangeGroupBy?: () => void
  showDelayMessage?: boolean
  buttonType?: 'primary' | 'ghost'
  buttonText?: string
  backButtonRoute?: string
  showFixedDates?: boolean
  onChangeFixedDate?: () => void
  reportPath?: string
  showCategory?: boolean
  showChildCategory?: boolean
  showBrand?: boolean
  showResetFilter?: boolean
  maxRangeMonths?: number
}

interface State {
  userPermittedLocations: ILocation[]
  visible: boolean
  categoryIds: string[]
  supplierIds: number[]
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class ReportFilter extends React.Component<Props, State> {
  productStore = this.props.currentCompanyStore?.productStore
  supplierStore = this.props.currentCompanyStore?.supplierStore

  constructor(props: Props) {
    super(props)

    this.state = {
      userPermittedLocations: [],
      visible: false,
      categoryIds: [],
      supplierIds: [],
    }
  }

  componentDidMount = () => {
    const locations = this.props.currentCompanyStore!.locations!
    const { permittedLocationIds } = this.props.reportFilterStore!
    const userPermittedLocations = locations.filter((location) =>
      permittedLocationIds.includes(location.id),
    )
    this.setState({ userPermittedLocations })
    this.productStore?.getCategories()
    this.supplierStore?.getSuppliers()
  }

  handleLocationCheckbox = (selectedLocationId: number) => {
    const filter = this.props.reportFilterStore!
    const { selectedLocationIds } = filter

    if (!selectedLocationIds) {
      return
    }

    const alreadyChecked = selectedLocationIds.includes(selectedLocationId)

    const newLocationIds = alreadyChecked
      ? selectedLocationIds.filter((locationId) => locationId !== selectedLocationId)
      : [...selectedLocationIds, selectedLocationId]

    filter.setSelectedLocationIds(newLocationIds)
  }

  handleLocationSelectAll = (all: boolean) => {
    if (all) {
      const userPermittedLocationIds = this.state.userPermittedLocations.map(
        (location) => location.id,
      )
      this.props.reportFilterStore!.setSelectedLocationIds(userPermittedLocationIds)
    } else {
      this.props.reportFilterStore!.setSelectedLocationIds([])
    }
  }

  onApply() {
    this.props.reportFilterStore?.setIsFirstLoad(false)

    // if apply function is passed into props, use it instead
    if (this.props.apply) {
      this.props.apply()
    } else {
      // Apply the local settings to the official report filter store, then apply
      this.props.reportFilterStore!.apply()
    }
  }

  handleVisibleChange = (visible: boolean) => {
    this.setState({ visible })
  }

  toReport = () => {
    const { reportPath, reportFilterStore } = this.props

    if (!reportFilterStore) {
      return
    }

    reportFilterStore.setIsFirstLoad(true)
    browserHistory.push({
      pathname: reportPath,
      query: {
        startDate: reportFilterStore.startDateString,
        endDate: reportFilterStore.endDateString,
        locationIds: reportFilterStore.selectedLocationIds,
      },
    })
  }

  get dropdownOverlayLocations(): JSX.Element {
    const filterStore = this.props.reportFilterStore!
    const { selectedLocationIds } = filterStore
    const { userPermittedLocations } = this.state
    return (
      <Menu>
        <Menu.Item key='all-none'>
          {userPermittedLocations.length > 1 && (
            <div className='text-right w-100 mb-2'>
              <a className='mr-2' onClick={() => this.handleLocationSelectAll(true)}>
                All
              </a>
              <a onClick={() => this.handleLocationSelectAll(false)}>None</a>
            </div>
          )}
        </Menu.Item>

        {userPermittedLocations.map((location) => {
          return (
            <Menu.Item key={location.id}>
              <Checkbox
                checked={selectedLocationIds.includes(location.id)}
                onClick={() => {
                  this.handleLocationCheckbox(location.id)
                }}
              >
                {location.name}
              </Checkbox>
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  selectFixedDays = (fixedDates: FixedDatesOptions) => {
    const filterStore = this.props.reportFilterStore
    filterStore?.setFixedDates(fixedDates)
    this.props.onChangeFixedDate?.()
  }

  get dropdownOverlayFixedDays(): JSX.Element {
    return (
      <Menu>
        {Object.values(FixedDatesOptions).map((fixedDates, index) => {
          return (
            <Menu.Item key={index} onClick={() => this.selectFixedDays(fixedDates)}>
              <p style={{ width: '100%', margin: 0 }}>{fixedDates}</p>
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  selectGroupBy = (groupBy: GroupByOptions) => {
    const filterStore = this.props.reportFilterStore!

    filterStore.setGroupBy(groupBy)
    this.props.onChangeGroupBy && this.props.onChangeGroupBy()
  }

  get dropdownOverlayGroupBy(): JSX.Element {
    return (
      <Menu>
        {Object.values(GroupByOptions).map((groupBy, index) => {
          return (
            <Menu.Item key={index} onClick={() => this.selectGroupBy(groupBy)}>
              <Tooltip
                title={groupBy === GroupByOptions.WEEK ? 'Changes date to start of week' : ''}
                placement='right'
              >
                <p style={{ width: '100%', margin: 0 }}>{groupBy}</p>
              </Tooltip>
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  get dateRangeLimit(): number {
    return this.props.maxRangeMonths || dateRangeLimitInMonths
  }

  get isDateLimitExceeded(): boolean {
    const { showSingleDate, hideDates, reportFilterStore } = this.props

    if (showSingleDate || hideDates) {
      return false
    }

    const { startDate, endDate } = reportFilterStore!

    const start = moment(startDate)
    const end = moment(endDate)

    const monthsBetweenChosenRange = end.diff(start, 'month', true)

    return monthsBetweenChosenRange > this.dateRangeLimit
  }

  get buttonText(): string {
    const { buttonText, reportPath } = this.props

    const shouldDisableButton = !reportPath && this.isDateLimitExceeded

    if (shouldDisableButton) {
      return `Range exceeded. Please filter by up to ${this.dateRangeLimit} months.`
    }

    if (buttonText) {
      return buttonText
    }

    return 'Apply'
  }

  resetFilters = () => {
    const filterStore = this.props.reportFilterStore
    filterStore?.reset()
  }

  onClearCategoryFilters = () => {
    const filterStore = this.props.reportFilterStore
    filterStore?.setSelectedParentCategoryIds([])
    filterStore?.setSelectedChildCategoryIds([])
  }
  handleCategoriesSelectAll = (all: boolean) => {
    const filterStore = this.props.reportFilterStore
    const flattenedCategories = this.productStore?.flattenedCategories

    const allCategoryIds = flattenedCategories?.map((category) => category.id)

    if (all && allCategoryIds) {
      filterStore?.setSelectedChildCategoryIds(allCategoryIds)
    } else {
      filterStore?.setSelectedParentCategoryIds([])
      filterStore?.setSelectedChildCategoryIds([])
    }
  }

  handleSupplierSelectAll = (all: boolean) => {
    const filterStore = this.props.reportFilterStore
    const suppliersWithName = this.supplierStore?.suppliersWithName

    const allSupplierIds = suppliersWithName?.map((supplierId) => supplierId.id)

    if (all && allSupplierIds) {
      filterStore?.setSelectedSupplierIds(allSupplierIds)
    } else {
      filterStore?.setSelectedSupplierIds([])
    }
  }
  onSelectCategoryId = (categoryId: string) => {
    const filterStore = this.props.reportFilterStore
    const newParentIds = new Set(filterStore?.parentCategoryIds)
    const newChildIds = new Set(filterStore?.childCategoryIds)

    const categoryFound = this.productStore?.mappedFlattenedCategories.get(categoryId)
    if (!categoryFound) {
      return
    }

    if (!categoryFound.parentCategoryId) {
      const cascadedChildCategoryIds = (this.productStore?.flattenedCategories || [])
        .filter((category) => category.parentCategoryId === categoryFound.id)
        .map((category) => category.id)

      newParentIds.add(categoryId)
      cascadedChildCategoryIds.forEach((categoryId) => newChildIds.add(categoryId))
    } else {
      newParentIds.add(categoryFound.parentCategoryId)
      newChildIds.add(categoryId)
    }

    filterStore?.setSelectedParentCategoryIds(Array.from(newParentIds))
    filterStore?.setSelectedChildCategoryIds(Array.from(newChildIds))
  }
  onDeselectCategoryIds = (categoryId: string) => {
    const filterStore = this.props.reportFilterStore
    const newParentIds = new Set(filterStore?.parentCategoryIds)
    const newChildIds = new Set(filterStore?.childCategoryIds)

    const categoryFound = this.productStore?.mappedFlattenedCategories.get(categoryId)
    if (!categoryFound) {
      return
    }
    const isAlreadyInChildIds = filterStore?.childCategoryIds.find((c) => c === categoryId)
    if (isAlreadyInChildIds) {
      newChildIds.delete(categoryId)
    } else if (!categoryFound.parentCategoryId) {
      const cascadedChildCategoryIds = (this.productStore?.flattenedCategories || [])
        .filter((category) => category.parentCategoryId === categoryFound.id)
        .map((category) => category.id)

      newParentIds.delete(categoryFound.id)
      cascadedChildCategoryIds.forEach((categoryId) => newChildIds.delete(categoryId))
    } else {
      newChildIds.delete(categoryId)
    }

    filterStore?.setSelectedParentCategoryIds(Array.from(newParentIds))
    filterStore?.setSelectedChildCategoryIds(Array.from(newChildIds))
  }
  onSelectSupplierIds = (supplierIds: number | undefined) => {
    const filterStore = this.props.reportFilterStore
    const selectedSupplierIds = new Set(filterStore?.supplierIds)

    if (supplierIds) {
      selectedSupplierIds.add(supplierIds)
    }
    filterStore?.setSelectedSupplierIds(Array.from(selectedSupplierIds))
  }
  onDeselectSupplierIds = (supplierIds: number | undefined) => {
    const filterStore = this.props.reportFilterStore
    const selectedSupplierIds = new Set(filterStore?.supplierIds)

    if (supplierIds) {
      selectedSupplierIds.delete(supplierIds)
    }
    filterStore?.setSelectedSupplierIds(Array.from(selectedSupplierIds))
  }

  onClearSupplierFilters = () => {
    const filterStore = this.props.reportFilterStore
    filterStore?.setSelectedSupplierIds([])
  }
  render() {
    const filterStore = this.props.reportFilterStore!
    const { startDate, endDate, selectedLocationIds, groupBy, fixedDates } = filterStore
    const { userPermittedLocations } = this.state
    const { showGroupBy, showDelayMessage, backButtonRoute, reportPath, showFixedDates } =
      this.props

    const showReportLink = reportPath && this.isDateLimitExceeded
    const flattenedCategories = this.productStore?.flattenedCategories

    const filterOption: SelectProps<object>['filterOption'] = (input, option) => {
      const optionName = option?.props.children

      if (optionName && typeof optionName === 'string') {
        const lowerCaseInput = input.toLowerCase()

        return optionName.toLowerCase().indexOf(lowerCaseInput) >= 0
      }

      return true
    }
    const suppliersWithName = this.supplierStore?.suppliersWithName
    const parsedSupplierIds = suppliersWithName?.map((supplier) => [supplier.id, supplier.name])

    return (
      <div
        className='p-3 mb-3 rounded background-white d-flex'
        style={{ justifyContent: 'space-between' }}
      >
        <div className='d-flex' style={{ flexWrap: 'wrap', gap: 16 }}>
          {backButtonRoute && (
            <BlazeButton
              buttonProps={{
                icon: 'arrow-left',
                onClick: () => {
                  browserHistory.replace(backButtonRoute)
                },
              }}
              titleProps={{
                title: 'Back',
              }}
            />
          )}

          <Dropdown
            trigger={['click']}
            overlay={this.dropdownOverlayLocations}
            placement='bottomCenter'
            onVisibleChange={this.handleVisibleChange}
            visible={this.state.visible}
          >
            <Button>
              {selectedLocationIds.length === userPermittedLocations.length ? (
                <span>Locations (all)</span>
              ) : (
                <span>Locations ({selectedLocationIds.length})</span>
              )}
              <Icon type='down' />
            </Button>
          </Dropdown>

          {!this.props.hideDates && (
            <DateFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(date) => filterStore.setStartDate(date)}
              onEndDateChange={(date) => filterStore.setEndDate(date)}
              maxRangeMonths={this.dateRangeLimit}
            />
          )}

          {this.props.showSingleDate && (
            <DatePicker
              defaultValue={moment(startDate)}
              onChange={(date) => {
                const newStartDate = date === null ? new Date() : date.toDate()
                filterStore.setStartDate(newStartDate)
              }}
            />
          )}

          {showGroupBy && (
            <Dropdown
              trigger={['click']}
              overlay={this.dropdownOverlayGroupBy}
              placement='bottomCenter'
            >
              <Button>
                <span>Group by {groupBy}</span>
                <Icon type='down' />
              </Button>
            </Dropdown>
          )}
          {showFixedDates && (
            <Dropdown
              trigger={['click']}
              overlay={this.dropdownOverlayFixedDays}
              placement='bottomCenter'
            >
              <Button>
                <span> Sales date range ({fixedDates})</span>
                <Icon type='down' />
              </Button>
            </Dropdown>
          )}
          {this.props.showChildCategory && (
            <NestedCategoryFilter
              className='gr-filter-item gr-filter-gutter'
              style={{ width: 165 }}
              flatCategories={flattenedCategories || []}
              selectedCategoryIds={[
                ...filterStore.parentCategoryIds,
                ...filterStore.childCategoryIds,
              ]}
              showAllandNone={true}
              onSelectAll={this.handleCategoriesSelectAll}
              onSelect={this.onSelectCategoryId}
              onDeselect={this.onDeselectCategoryIds}
              onClear={() => {
                this.onClearCategoryFilters()
              }}
            />
          )}
          {this.props.showBrand && (
            <Select<number[]>
              className='gr-filter-item gr-filter-gutter'
              showSearch
              style={{ width: 165 }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ maxWidth: 250 }}
              maxTagPlaceholder={`Suppliers (${
                filterStore.supplierIds.length === parsedSupplierIds?.length
                  ? 'all'
                  : filterStore.supplierIds?.length
              })`}
              maxTagTextLength={4}
              value={filterStore.supplierIds}
              showArrow={true}
              placeholder='Select brands'
              mode='multiple'
              maxTagCount={0}
              filterOption={filterOption}
              onSelect={this.onSelectSupplierIds}
              onDeselect={this.onDeselectSupplierIds}
            >
              {filterStore.supplierIds && (
                <Select.Option key='all-none' value='' disabled>
                  <div className='text-right w-100 mb-1 cursor-pointer'>
                    <a className='mr-2' onClick={() => this.handleSupplierSelectAll(true)}>
                      All
                    </a>
                    <a onClick={() => filterStore.setSelectedSupplierIds([])}>None</a>
                  </div>
                </Select.Option>
              )}

              {suppliersWithName?.map((supplier) => (
                <Select.Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Select.Option>
              ))}
            </Select>
          )}
          {this.props.showResetFilter && (
            <Button type='link' onClick={() => this.resetFilters()}>
              Reset
            </Button>
          )}
          {showReportLink ? (
            <Alert
              message={
                <a
                  style={{ color: 'black', textDecorationLine: 'underline' }}
                  onClick={this.toReport}
                >
                  Range exceeded {this.dateRangeLimit} month limit, please use{' '}
                  <strong>Report </strong>
                  <ArrowRightOutlined />
                </a>
              }
              type='success'
              style={{
                height: '33px',
                padding: '6px 10px 6px 13px',
              }}
            />
          ) : (
            <BlazeButton
              buttonProps={{
                onClick: () => this.onApply(),
                type: this.props.buttonType || 'primary',
                disabled: this.isDateLimitExceeded,
              }}
              titleProps={{
                title: this.buttonText,
              }}
            />
          )}
        </div>
        {showDelayMessage && (
          <div style={{ flexDirection: 'row-reverse' }} className='text-muted pt-1'>
            Up to 1 hour delay
          </div>
        )}
        {this.props.infoMessage && (
          <Alert
            className='mt-3 report-categories-on-hand'
            message={this.props.infoMessage}
            type='info'
            showIcon
          />
        )}
      </div>
    )
  }
}
